import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useFloatingPosition from '../hooks/useFloatingPosition';
import AppIcon from './AppIcon';
import DragHandle from './DragHandle';

function QuickLaunchItem({ app, editMode, overflow = false, onEdit, onRemove }) {
  const sortable = useSortable({
    id: `quick-${app.id}`,
    data: { type: 'quickLaunch', appId: app.id },
    disabled: !editMode
  });
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipAnchorRef = useRef(null);
  const tooltipRef = useRef(null);
  const closeTooltip = useCallback(() => setTooltipOpen(false), []);
  const tooltipStyle = useFloatingPosition({
    open: tooltipOpen && !overflow,
    anchorRef: tooltipAnchorRef,
    floatRef: tooltipRef,
    align: 'center',
    onDismiss: closeTooltip
  });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition
  };
  const className = `quick-launch-item${overflow ? ' quick-launch-item-overflow' : ''}${sortable.isDragging ? ' is-dragging' : ''}`;

  const content = (
    <>
      {editMode && (
        <DragHandle
          ref={sortable.setActivatorNodeRef}
          label={`Reorder ${app.name} in Quick Launch`}
          {...sortable.attributes}
          {...sortable.listeners}
        />
      )}
      <AppIcon app={app} />
      {overflow && <span className="quick-launch-overflow-name">{app.name}</span>}
      {editMode && (
        <button
          type="button"
          className="quick-launch-remove"
          aria-label={`Remove ${app.name} from Quick Launch`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          ×
        </button>
      )}
    </>
  );

  return (
    <>
      <div
        ref={sortable.setNodeRef}
        style={style}
        className={className}
        data-tooltip={!editMode && !overflow ? app.name : undefined}
        data-portal-tooltip={!overflow ? 'true' : undefined}
        data-edit-mode={editMode ? 'true' : undefined}
        tabIndex={-1}
      >
        {editMode ? (
          <div
            ref={!overflow ? tooltipAnchorRef : undefined}
            className="quick-launch-link"
            role="button"
            tabIndex={0}
            aria-label={`Edit ${app.name}`}
            onClick={(event) => {
              if (event.target.closest('button')) return;
              onEdit?.();
            }}
            onKeyDown={(event) => {
              if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                onEdit?.();
              }
            }}
            onMouseEnter={() => !overflow && setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
            onFocus={() => !overflow && setTooltipOpen(true)}
            onBlur={() => setTooltipOpen(false)}
          >
            {content}
          </div>
        ) : (
          <a
            ref={!overflow ? tooltipAnchorRef : undefined}
            className="quick-launch-link"
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${app.name} in a new tab`}
            aria-describedby={tooltipOpen && !overflow ? `quick-tooltip-${app.id}` : undefined}
            onMouseEnter={() => !overflow && setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
            onFocus={() => !overflow && setTooltipOpen(true)}
            onBlur={() => setTooltipOpen(false)}
          >
            {content}
          </a>
        )}
      </div>
      {tooltipOpen && !overflow && createPortal(
        <span
          id={`quick-tooltip-${app.id}`}
          className="quick-launch-tooltip"
          role="tooltip"
          ref={tooltipRef}
          style={tooltipStyle}
        >
          {app.name}
        </span>,
        document.body
      )}
    </>
  );
}

export default QuickLaunchItem;